import { defineStore } from 'pinia'
import { computed, ref, toValue } from 'vue'
import { usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { Error, FAILED_REPLY_TEXT, makeRuleset, parseAxiosError, Phase, Round, Ruleset, useSessionData } from '@/objects'
import { echo } from '@/echo'
import { env } from '@/utils'
import { AxiosError } from 'axios'
import { handleError } from './AppStore'

export type ProbConfAnswer = {
    ruleset: Ruleset,
    // Point mode: probability of action A (0-100) and confidence (0-1).
    prob: number | null
    conf: number
    // Distribution mode: token counts per probability bin, summing to 100. Always present
    // (initialized to zeros); only sent to the server in distribution mode.
    distribution: number[]
}

export const useParticipantStore = defineStore('participant', () => {
    //state
    const { state, stateInit } = useSessionData()
    const initialized = ref(false)

    /**
     * Callback to run when the opponent's action is received.
     * Runs after checking if it's a valid signal and after updating the state
     */
    const onAction = ref<(choice: string) => void>()
    /**
     * Callback to run when the consultant's reply is received.
     * Runs after checking if it's a valid signal and after updating the state
     */
    const onReply = ref<(replyTxt: string) => void>()
    /**
     * Callback to run when the midway vote is started.
     * Runs after checking if it's a valid signal and after updating the state
     */
    const onVoteStart = ref<() => void>()
    /**
     * Callback to run when the midway vote is completed.
     * Runs after checking if it's a valid signal and after updating the state
     */
    const onVoteDone = ref<(ruleset: Ruleset) => void>()

    /**
     * Callback to run when the participant disconnects
     */
    const onDisconnected = ref<() => void>()

    /**
     * Callback to run on any error (usually as a response from the backend)
     * The frontent should use this to set the injected error ref from App.vue
     */
    const onError = ref<(err: Error) => void>()

    /**
     * Timestamp (ms) of the last activity that refreshes the server-side last_action_at
     * (message/action/vote/feedback). Initialized from the server's value (lastActionAt in
     * the session payload, which starts at the session's opened_at), so the inactivity
     * countdown shows the same synchronous clock the disconnection pruner enforces — a late
     * joiner or a page refresh sees an already partially elapsed timer, not a fresh one.
     * Deliberately NOT resettable any other way: the timeout bounds the synchronous
     * session's length, so only real progress counts.
     */
    const lastActivity = ref(Date.now())

    function markActivity() {
        lastActivity.value = Date.now()
    }

    //getters
    const currentRound = computed<Round>(() => {
        return state.currentRound
    })
    
    const currentPhase = computed<Phase>(() => {
        return state.currentPhase
    })

    function addRound(r?: Round) {
        return currentPhase.value.addRound(r ?? new Round(currentPhase.value.rounds.length, [], state.hasChat))
    }

    //actions
    function initialize() {
        if (initialized.value) {
            console.warn("ParticipantStore state already initialized!")
            return
        }

        const data = usePage().props.chatSessionData;
        if (!data)
            return;

        stateInit(data)

        // Start the inactivity anchor from the server's deadline snapshot (it accounts for
        // when the participant actually became able to act); local stage transitions and own
        // actions take over from there.
        if (data["timerActive"] && data["timerAnchor"]) {
            lastActivity.value = Number(data["timerAnchor"]) * 1000
        }

        initialized.value = true

        listenTo("ActionSent", (e) => {
            const choice : string = e["action"].choice
            console.log(`Received action: ${env().DEV ? JSON.stringify(choice) : ""}`)

            const phase = currentPhase.value
            const num = e["round_number"]
            if (!phase || !num) {
                console.warn("Shouldn't have received an action now!!")
                return
            }

            // Apply the opponent's choice to the exact round it was made on (not just
            // "the current round"), creating the round locally if they're a step ahead.
            while (phase.rounds.length < num) {
                phase.addRound(new Round(phase.rounds.length, [], state.hasChat))
            }

            phase.rounds[num - 1].choices[1] = choice

            if (onAction.value) {
                onAction.value(choice)
            }
        })
        
        listenTo("ReplySent", (e) => {
            if (!state.hasChat) {
                console.warn("Reply received with no chat!! How?!")
                return
            }
            
            if (!currentRound.value.awaitingReply) {
                console.warn("Why did the consultant reply??")
                return
            }

            // A failed LLM reply arrives with an empty body: show why instead of a blank bubble.
            const reply = e["reply"].has_error ? FAILED_REPLY_TEXT : e["reply"].body

            console.log(`Received reply '${reply}'`)
            
            currentRound.value.addMessage(reply)
            
            if (onReply.value) {
                onReply.value(reply)
            }
        })

        listenTo("VoteStarted", () => {
            if (state.voteDone) {
                console.warn("Vote is already done!!")
                return
            }

            console.log(`Vote started`)

            // The vote is now open: the server has started counting the vote deadline. Arm the
            // client's vote clock immediately, whether or not the participant has navigated off
            // the result board / "wait for everyone" modal, so the countdown and warning show
            // and they can't be silently kicked while still on those screens.
            state.voteAvailable = true

            if (onVoteStart.value) {
                onVoteStart.value()
            }
        })

        listenTo("VoteComplete", (e) => {
            if (state.voteDone || state.phase2.ruleset) {
                console.warn("Vote is already done!!")
                return
            }

            console.log(`Vote done`)

            const rs = makeRuleset(e["ruleset"])
            state.phase2.ruleset = rs

            // Receiving VoteComplete is definitive proof the vote round is over. 
            // Mark it done before navigating so the route guards let us through even if this
            // (last) voter's own /votes response hasn't resolved yet.
            state.voteDone = true

            if (onVoteDone.value) {
                onVoteDone.value(rs)
            }
        })

        listenTo("Disconnected", (e) => {
            if (e["part_id"] !== state.partId) {
                console.warn("I'm not disconnecting!!")
                return
            }

            //TODO: Store the disconnected state somewhere maybe

            if (onDisconnected.value) {
                onDisconnected.value()
            }
        })
    }

    function handleErr(caller: string, err: AxiosError) {
        handleError(err, toValue(onError), caller)
    }

    async function sendMessage(messageBody: string) {
        markActivity()
        if (!currentRound.value) {
            console.error("Invalid current round!")
            return
        }

        // Optimistically show the message; if the privacy filter rejects it we move it out
        // of the delivered stream below so the participant can immediately retry.
        currentRound.value.messages.push(messageBody)

        try {
            const { data } = await axios.post('/messages', { body: messageBody })

            if (data.blocked) {
                currentRound.value.messages.pop()
                currentRound.value.blockedMessages.push({
                    body: messageBody,
                    after: currentRound.value.messages.length,
                })

                return { ok: true, blocked: true, message: data }
            }

            return { ok: true, blocked: false, message: data }

        } catch (err) {
            currentRound.value.messages.pop()

            handleErr('sendMessage', err)

            return {
                ok: false,
                message: 'Ops! Qualcosa è andato storto. Riprova più tardi.',
            }
        }
    }

    async function sendAction(choice: string) {
        markActivity()
        if (!currentRound.value) {
            console.error("Invalid current round!")
            return
        }

        // Pin the round now: by the time the response lands the opponent's ActionSent may
        // have completed it and moved currentRound on.
        const round = currentRound.value
        round.choices[0] = choice

        try {
            const { data } = await axios.post('/actions', { choice })

            // The server hands back the opponent's choice when it already exists — made while
            // this page was loading (its ActionSent then predates our subscription and is
            // lost) or by a substitute (which never broadcasts). The caller's advance() picks
            // the completed round up, as it would after an ActionSent.
            if (data.partner_choice && !round.choices[1]) {
                round.choices[1] = data.partner_choice
            }

            return { ok: true, choice: data.choice }

        } catch (err) {
            handleErr('sendAction', err)
            
            return {
                ok: false,
                message: 'Ops! Qualcosa è andato storto. Riprova più tardi.',
            }
        }
    }

    async function sendVote(pcAnswers: ProbConfAnswer[], votedRSId: number, rating?: string, voteDurationMs: number | null = null) {
        markActivity()
        const sessionData = usePage().props.chatSessionData
        const isDistribution = sessionData['predictionMode'] === 'distribution'

        const data = {
            predictions: pcAnswers.map((pca) => {
                if (isDistribution) {
                    return {
                        "ruleset": pca.ruleset.id,
                        "distribution": pca.distribution,
                    }
                }
                return {
                    "ruleset": pca.ruleset.id,
                    "prob": Number(pca.prob)/100,
                    "conf": pca.conf
                }
            }),
            ruleset_voted_id: votedRSId,
            consultant_feedback: state.hasChat ? rating : null,
            // Time spent on the table-vote step, measured by the vote page.
            vote_duration_ms: voteDurationMs,
        }

        console.log(`Sending votes & feedback: ${JSON.stringify(data)}`)

        // Remember which table we voted for so phase 2 can show it in the vote summary
        // (the session data, parsed once at load, predates this vote).
        const votedRs = [sessionData['pdRuleset'], sessionData['hgRuleset']]
            .find((rs) => rs.id === data["ruleset_voted_id"])
        
        try {
            await axios.post("/votes", data)

            // Only mark the vote as done once it is actually persisted, otherwise the
            // router guards (which now read this state) would wave the participant
            // through to phase 2 on a failed vote.
            state.votedRuleset = makeRuleset(votedRs)
            state.otherActionPredictionAnswered = true
            state.phase1.evaluated = true

            return { ok: true }
        } catch (error) {
            handleErr('sendVote', error)

            return {
                ok: false,
                message: 'Ops! Qualcosa è andato storto. Riprova più tardi.',
            }
        }
    }

    async function sendFeedback(rating: string) {
        markActivity()
        if (!state.hasChat) {
            console.error("Can't send feedback without a consultant!")
            return {
                ok: false
            }
        }

        try {
            await axios.post('/consultant-feedback', { feedback: rating })

            // Mark phase 2's consultant as evaluated only once persisted, so the route
            // guards don't wave us past the questionnaire on a failed submit.
            state.phase2.evaluated = true

            return { ok: true }
        } catch (error) {
            handleErr('sendFeedback', error)

            return {
                ok: false,
                message: 'Ops! Qualcosa è andato storto. Riprova più tardi.',
            }
        }
    }

    async function completeQuestionnaire() {
        try {
            await axios.post('/questionnaire')

            // Only flip the flag once persisted, so the route guards keep the participant
            // on the questionnaire if the submit failed.
            state.questionnaireCompleted = true

            return { ok: true }
        } catch (error) {
            handleErr('completeQuestionnaire', error)
            
            return {
                ok: false,
                message: 'Ops! Qualcosa è andato storto. Riprova più tardi.',
            }
        }
    }

    /**
     * Persist completion of the login-time (Part 1) onboarding flow. The local flag is flipped
     * regardless of the outcome: the participant has genuinely read the flow, so a failed POST
     * must not trap them behind it. The only cost of a failure is seeing it again on a reload,
     * which is far better than an error banner blocking the very first screen — hence the
     * console-only reporting instead of handleErr().
     */
    async function completeOnboarding() {
        state.onboardingCompleted = true

        try {
            await axios.post('/onboarding')
        } catch (error) {
            console.error('completeOnboarding', error)
        }
    }

    function listenTo(event: string, callback: (e: Object) => void) {
        return echo.private(`participant.${state.partId}`).listen(event, callback)
    }

    return {
        state,
        initialize,
        sendMessage,
        sendAction,
        addRound,
        sendVote,
        sendFeedback,
        completeQuestionnaire,
        completeOnboarding,
        onAction,
        onReply,
        onVoteStart,
        onVoteDone,
        onDisconnected,
        onError,
        currentPhase,
        currentRound,
        listenTo,
        lastActivity,
        markActivity
    }
})
